import { useEffect, useState } from 'react';

import Modal from '../../../../../components/Modal/modal';
import TextInput from '../../../../../components/ui/TextInput';
import Button from '../../../../../components/ui/button';
import { useUserProfileStore } from '../../../../../store/user';

import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import Toast from '../../../../../components/ui/toast';
import { sentryCapture } from '../../../../../helpers/utils';
import { useApi } from '../../../../../hooks/useApi';
import { useTokenStore } from '../../../../../store/token';
import { SentryError } from '../../../../../types/enums';
import { VerifyIndentity } from '../../../../../types/userData/validation';

interface iProps {
	open: boolean;
	onFinish: () => void;
	onClose: () => void;
}

export const VerifyIdentity = ({ open, onClose, onFinish }: iProps) => {
	const api = useApi();
	const userData = useUserProfileStore((state: any) => state.userData);

	const token = useTokenStore((state: any) => state.token);
	const { updateUserData } = useUserProfileStore();
	const [isLoading, setIsLoading] = useState(false);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm<z.infer<typeof VerifyIndentity>>({
		resolver: zodResolver(VerifyIndentity),
		mode: 'onChange',
		defaultValues: {
			fullname: '',
			documentNumber: '',
			dateOfBirth: '',
		},
	});

	useEffect(() => {
		if (userData?.name) {
			setValue('fullname', userData?.name);
		}
		if (userData?.birthAt) {
			const formatDate = format(
				new Date(`${userData?.birthAt}T00:00:00`),
				'yyyy-MM-dd'
			);

			setValue(
				'dateOfBirth',
				format(new Date(`${formatDate}T00:00:00`), 'dd/MM/yyyy')
			);
		}
		if (userData?.documentNumber) {
			setValue('documentNumber', userData?.documentNumber);
		}
	}, []);

	const handleUpdate = async (data: z.infer<typeof VerifyIndentity>) => {
		setIsLoading(true);
		const body = {
			name: data?.fullname?.toLowerCase(),
			birthAt: data?.dateOfBirth,
			documentNumber: data?.documentNumber,
		};
		try {
			const response = await api.updateIdentity(body, token);
			if (!response?.success) {
				throw new Error(response.message);
			}

			let birthAt = null;
			if (body?.birthAt) {
				const [day, month, year] = body?.birthAt.split('/');
				birthAt = `${year}-${month}-${day}`;
			}

			updateUserData('name', data?.fullname?.toLowerCase());
			updateUserData('documentNumber', data?.documentNumber);
			updateUserData('birthAt', birthAt);
			updateUserData('pixKey', {
				type: '0',
				value: data?.documentNumber,
			});
			reset({ fullname: '', documentNumber: '', dateOfBirth: '' });
			onFinish();
			onClose();
		} catch (error: any) {
			toast(<Toast variant="error">{error.message}</Toast>);
			sentryCapture(error, 'sendTokenValidation', SentryError.Error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="flex items-center gap-4">
			<Modal id="verifyIndentity" open={open} onClose={onClose}>
				<div className="">
					<div className="flex flex-col pb-5">
						<p className="text-xl font-bold text-center text-custom-gray2 font-bahnschrift">
							Atualize seus dados
						</p>
						<p className="text-xs text-center text-custom-gray2 font-bahnschrift">
							Atualize seus dados para prosseguir com a transação.
						</p>
					</div>

					<form
						onSubmit={handleSubmit(handleUpdate)}
						className="flex flex-col"
					>
						<div className="flex flex-col gap-3 pb-6">
							<Controller
								control={control}
								name="fullname"
								render={({ field: { value, onChange } }) => (
									<TextInput
										type="text"
										placeholder="Nome completo"
										value={value}
										error={errors?.fullname?.message}
										onChange={onChange}
									/>
								)}
							/>
							<Controller
								control={control}
								name="dateOfBirth"
								render={({ field: { value, onChange } }) => (
									<TextInput
										type="text"
										mask="99/99/9999"
										placeholder="Data de nascimento"
										value={value}
										error={errors?.dateOfBirth?.message}
										onChange={onChange}
										inputMode="tel"
									/>
								)}
							/>
							<Controller
								control={control}
								name="documentNumber"
								render={({ field: { value, onChange } }) => (
									<TextInput
										type="text"
										mask="999.999.999-99"
										placeholder="CPF"
										value={value}
										error={errors?.documentNumber?.message}
										onChange={onChange}
										inputMode="tel"
									/>
								)}
							/>
						</div>
						<Button
							type="submit"
							disabled={
								!!errors?.fullname ||
								!!errors?.documentNumber ||
								!!errors?.dateOfBirth
							}
							isLoading={isLoading}
							className="px-5 py-3 font-bahnschrift text-center float-left"
							textCustom="text-sm"
						>
							Enviar
						</Button>
						<p className="text-xs text-left pt-2 text-custom-gray3 font-bahnschrift">
							A pessoa responsável pelos dados deve ser a mesma
							para as transações de depósito e saque.
						</p>
					</form>
				</div>
			</Modal>
		</div>
	);
};
